import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { FaTruck, FaTruckMoving, FaUser, FaUserAlt } from "react-icons/fa";
import InputCustom from "../../../../../../components/InputCustom";
import SelectCustom from "../../../../../../components/SelectCustom";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { IMensalista } from "../../types/types";
import formValidator from "../validators/formValidator";
import ChoiceBox from "./shared/ChoiceBox";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";

interface FormValues {
  placa: string;
  cnpj: string;
  id_transportadora: string;
  ativo: boolean;
  tipo_transportadora: string;
}

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: IMensalista;
  onConfirm: () => void;
  onClose: () => void;
}

const Form: React.FC<Props> = (props: Props) => {
  const [transportadoras, setTransportadoras] = useState<any[]>([]);
  const [selectedTransportadora, setSelectedTransportadora] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const [choiceOptions, setChoiceOptions] = useState([
    {
      id: 0,
      label: "CNPJ Avulso",
      icon: <FaUserAlt className="w-6 h-6 pr-2" color="white" />,
    },
    {
      id: 1,
      label: "Transportadora",
      icon: <FaTruck className="w-6 h-6 pr-2" />,
    },
  ]);

  const getTransportadoras = useCallback(async (searchTerm: string = "") => {
    try {
      const body = {
        qtd_por_pagina: 100,
        order_by: "razao_social",
        order_direction: "asc",
        ...(searchTerm.trim().length > 0
          ? { razao_social: searchTerm.trim() }
          : {}),
      };

      const response = await api.post("/listar/transportadoras", body);

      const mappingData = response.data.data.map((rows: any) => {
        return {
          value: rows.id_transportadora,
          label: rows.razao_social,
        };
      });

      return mappingData;
    } catch {
      return [];
    }
  }, []);

  const handleSubmit = useCallback(
    async (values: FormValues, row?: IMensalista) => {
      try {
        setLoading(true);

        const body = {
          id_mensalista: row?.id_mensalista,
          placa: values.placa.replace("-", ""),
          cnpj: values.cnpj.length > 0 ? values.cnpj.replaceAll('.', '').replace('/', '').replace('-', '') : null,
          id_transportadora: String(values.id_transportadora).length > 0 ? values.id_transportadora : null,
          ativo: true,
          id_usuario_historico: user?.id,
        };

        if (props.isEdit) {
          const response = await api.post("/editar/mensalistas", body);

          if(response.status === 200) {
            props.onConfirm();
          }
        } else {
          const response = await api.post("/cadastrar/mensalistas", body);

          if(response.status === 200) {
            props.onConfirm();
          }
        }

        setLoading(false);

      } catch {
        setLoading(false);
      }
    },
    [props]
  );

  const onLoadFormValues = useCallback((row: any) => {
    const data = row;

    if (data) {
      const hasTransportadora = data.mensalista_transportadora != null;

      formik.setFieldValue("placa", data.placa);
      formik.setFieldValue("cnpj", data.cnpj);
      formik.setFieldValue("tipo_transportadora", "0");
      if (hasTransportadora) {
        const transportadoraOption = {
          value: data.mensalista_transportadora.id_transportadora,
          label: data.mensalista_transportadora.razao_social_transportadora,
        };

        formik.setFieldValue(
          "id_transportadora",
          data.mensalista_transportadora.id_transportadora
        );
        setSelectedTransportadora(transportadoraOption);
        setTransportadoras((currentOptions: any[]) => {
          const alreadyExists = currentOptions.some(
            (option) => option.value === transportadoraOption.value
          );

          return alreadyExists
            ? currentOptions
            : [transportadoraOption, ...currentOptions];
        });
        formik.setFieldValue("tipo_transportadora", "1");
      } else {
        setSelectedTransportadora(null);
      }
      setChoiceOptions([
        {
          id: 0,
          label: "CNPJ Avulso",
          icon: hasTransportadora ? (
            <FaUser className="w-6 h-6 pr-2" />
          ) : (
            <FaUserAlt className="w-6 h-6 pr-2" color="white" />
          ),
        },
        {
          id: 1,
          label: "Transportadora",
          icon: hasTransportadora ? (
            <FaTruckMoving className="w-6 h-6 pr-2" color="white" />
          ) : (
            <FaTruck className="w-6 h-6 pr-2" />
          ),
        },
      ]);
      formik.setFieldValue("ativo", data.ativo);
    }
  }, []);

  const initialValues: FormValues = {
    placa: "",
    cnpj: "",
    id_transportadora: "",
    ativo: true,
    tipo_transportadora: "0",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: formValidator,
    onSubmit: (values: FormValues) => handleSubmit(values, props.selectedRow),
  });

  const onConfirm = () => {
    formik.handleSubmit();
  };

  const onClose = () => {
    props.onClose();
  };

  useEffect(() => {
    getTransportadoras().then((options) => setTransportadoras(options));
  }, [getTransportadoras]);

  useEffect(() => {
    if (props.isView || props.isEdit) {
      onLoadFormValues(props.selectedRow);
    }
  }, [props.isView, props.isEdit, props.selectedRow, onLoadFormValues]);

  const tipoTransportadora = Number(formik.values.tipo_transportadora);

  let transportadoraField = null;

  if (tipoTransportadora === 0) {
    transportadoraField = (
      <div className="w-full">
        <InputCustom
          id="cnpj"
          title="CNPJ Avulso"
          typeInput="text"
          placeholder="CNPJ Avulso"
          onChange={formik.handleChange("cnpj")}
          touched={formik.touched.cnpj}
          error={formik.errors.cnpj}
          value={formik.values.cnpj}
          disabled={props.isView}
        />
      </div>
    );
  } else if (tipoTransportadora === 1) {
    transportadoraField = (
      <div className="">
        <SelectCustom
          id="idTransportadora"
          title="Transportadora"
          async
          data={getTransportadoras}
          defaultOptions={transportadoras}
          defaultValue={selectedTransportadora}
          onChange={(selectedOption: any) => {
            setSelectedTransportadora(selectedOption);
            formik.setFieldValue(
              "id_transportadora",
              selectedOption?.value || ""
            );
          }}
          touched={formik.touched.id_transportadora}
          error={formik.errors.id_transportadora}
          disabled={props.isView}
          placeholder="Digite para buscar a transportadora"
        />
      </div>
    );
  }

  return (
    <>
      <Loading loading={loading} skeletonFields={3} skeletonColumns={1} />
      <div className="flex flex-col justify-between h-full">

      <div className="flex flex-col overflow-y-scroll max-w-[550px] p-5">
        <div className="grid grid-cols-1 gap-3 mb-4">
          <div className="w-1/2">
            <InputCustom
              id="placa"
              title="Placa"
              typeInput="mask"
              mask="aaa-9*99"
              placeholder="AAA-0000 OU AAA-0A00"
              onChange={formik.handleChange("placa")}
              touched={formik.touched.placa}
              error={formik.errors.placa}
              value={formik.values.placa}
              disabled={props.isView}
            />
          </div>
          <div>
            <ChoiceBox
              id="tipoTransportadora"
              data={choiceOptions}
              onChange={(value: number) => {
                console.log(value);
                formik.setFieldValue("tipo_transportadora", String(value));

                setChoiceOptions([
                  {
                    id: 0,
                    label: "CNPJ Avulso",
                    icon:
                      value === 0 ? (
                        <FaUserAlt className="w-6 h-6 pr-2" color="white" />
                      ) : (
                        <FaUser className="w-6 h-6 pr-2" />
                      ),
                  },
                  {
                    id: 1,
                    label: "Transportadora",
                    icon:
                      value === 1 ? (
                        <FaTruckMoving className="w-6 h-6 pr-2" color="white" />
                      ) : (
                        <FaTruck className="w-6 h-6 pr-2" />
                      ),
                  },
                ]);
              }}
              value={formik.values.tipo_transportadora}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-1 mb-4 p-1">
          {transportadoraField}
        </div>
      </div>
      <div className="w-full min-h-14 flex items-center justify-end bg-[#FFFFFF] shadow-xl">
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#F9FAFA] text-sm text-[#000000] font-bold rounded-full mr-2"
          style={{ border: "1px solid #DBDEDF" }}
          onClick={() => onClose()}
          id="btnClose"
        >
          Cancelar
        </button>
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full mr-2"
          onClick={() => onConfirm()}
          id="btnSave"
        >
          Salvar
        </button>
      </div>
      </div>

    </>
  );
};

export default Form;
